/**
 * Actions for the /admin/app-config definition sync.
 *
 * Every mutation carries its `configKey` through request, success and failure.
 * The ticket asks for per-row success/failure rather than one global toast, and
 * a failure that cannot name its row cannot be rendered on it.
 */
import { createAction } from '@reduxjs/toolkit';
import {
  KeyedFailure,
  LiveDefinition,
  ManifestDefinition,
  RootOrgStatus,
} from '../models';

export const SCAN_DEFINITIONS_REQUEST = createAction(
  'ADMIN/SCAN_DEFINITIONS_REQUEST'
);
export const SCAN_DEFINITIONS_SUCCESS = createAction<{
  readonly records: readonly LiveDefinition[];
  /** True when the definitions read but this org's values did not. */
  readonly valuesUnavailable: boolean;
  readonly scannedAt: string;
}>('ADMIN/SCAN_DEFINITIONS_SUCCESS');
export const SCAN_DEFINITIONS_FAILURE = createAction<{
  readonly message: string;
}>('ADMIN/SCAN_DEFINITIONS_FAILURE');

export const CHECK_ROOT_ORGANIZATION_REQUEST = createAction(
  'ADMIN/CHECK_ROOT_ORGANIZATION_REQUEST'
);
export const CHECK_ROOT_ORGANIZATION_RESULT = createAction<{
  readonly status: RootOrgStatus;
  readonly organizationName?: string;
}>('ADMIN/CHECK_ROOT_ORGANIZATION_RESULT');

export const ADD_DEFINITIONS_REQUEST = createAction<{
  readonly definitions: readonly ManifestDefinition[];
}>('ADMIN/ADD_DEFINITIONS_REQUEST');
export const ADD_DEFINITIONS_SUCCESS = createAction<{
  readonly configKeys: readonly string[];
}>('ADMIN/ADD_DEFINITIONS_SUCCESS');
export const ADD_DEFINITIONS_FAILURE = createAction<KeyedFailure>(
  'ADMIN/ADD_DEFINITIONS_FAILURE'
);

export const UPDATE_DEFINITION_REQUEST = createAction<{
  readonly definition: ManifestDefinition;
}>('ADMIN/UPDATE_DEFINITION_REQUEST');
export const UPDATE_DEFINITION_SUCCESS = createAction<{
  readonly configKey: string;
}>('ADMIN/UPDATE_DEFINITION_SUCCESS');
export const UPDATE_DEFINITION_FAILURE = createAction<KeyedFailure>(
  'ADMIN/UPDATE_DEFINITION_FAILURE'
);

/**
 * Sets a definition's default to something the manifest does NOT say.
 *
 * Separate from UPDATE_DEFINITION_REQUEST, which restores a drifted key TO the
 * manifest and sends every field. This sends one field and leaves the rest
 * alone: the operator is choosing a per-environment value, not asking for the
 * manifest back, and sending `configType` alongside would quietly "repair"
 * things they did not ask about.
 *
 * Shares the UPDATE_DEFINITION_SUCCESS/FAILURE pair — the outcome is the same
 * shape and the row reports it the same way.
 */
export const SET_DEFAULT_REQUEST = createAction<{
  readonly configKey: string;
  /** One or the other, chosen by configType. Never both. */
  readonly defaultValue?: string;
  readonly defaultValueJSON?: unknown;
}>('ADMIN/SET_DEFAULT_REQUEST');

export const DELETE_DEFINITION_REQUEST = createAction<{
  readonly configKey: string;
}>('ADMIN/DELETE_DEFINITION_REQUEST');
export const DELETE_DEFINITION_SUCCESS = createAction<{
  readonly configKey: string;
}>('ADMIN/DELETE_DEFINITION_SUCCESS');
export const DELETE_DEFINITION_FAILURE = createAction<KeyedFailure>(
  'ADMIN/DELETE_DEFINITION_FAILURE'
);

/**
 * Drops the transient success markers once they have been seen. Plural: a bulk
 * register sets one per key and they all expire together.
 */
export const CLEAR_DEFINITION_SUCCESS = createAction<{
  readonly configKeys: readonly string[];
}>('ADMIN/CLEAR_DEFINITION_SUCCESS');

/** Clears a row's error, e.g. when the admin retries it. */
export const CLEAR_DEFINITION_ERROR = createAction<{
  readonly configKey: string;
}>('ADMIN/CLEAR_DEFINITION_ERROR');

export const actionScanDefinitions = () => SCAN_DEFINITIONS_REQUEST();
export const actionCheckRootOrganization = () =>
  CHECK_ROOT_ORGANIZATION_REQUEST();
export const actionAddDefinitions = (
  definitions: readonly ManifestDefinition[]
) => ADD_DEFINITIONS_REQUEST({ definitions });
export const actionUpdateDefinition = (definition: ManifestDefinition) =>
  UPDATE_DEFINITION_REQUEST({ definition });
export const actionDeleteDefinition = (configKey: string) =>
  DELETE_DEFINITION_REQUEST({ configKey });
export const actionSetDefault = (payload: {
  readonly configKey: string;
  readonly defaultValue?: string;
  readonly defaultValueJSON?: unknown;
}) => SET_DEFAULT_REQUEST(payload);
